import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ThemeConfig, ThemeConfigMap } from '../models/theme.model';
import { Logger } from './logger.service';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly configUrl = 'assets/config/theme.config.json';

  private themes = signal<ThemeConfigMap>({});
  private activeThemeKey = signal<string>('compass-default');

  /** Reactive access to the current theme config */
  readonly activeTheme = computed<ThemeConfig | undefined>(() => {
    return this.themes()[this.activeThemeKey()];
  });

  readonly companyName = computed(() => this.activeTheme()?.companyName ?? 'Compass');
  readonly logoUrl = computed(() => this.activeTheme()?.logoUrl ?? 'assets/images/compass-logo.svg');
  readonly logoAlt = computed(() => this.activeTheme()?.logoAlt ?? 'Compass');

  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);

  /** Load themes from the config JSON (call once at app init) */
  loadThemes(): Promise<void> {
    return new Promise((resolve) => {
      this.http.get<ThemeConfigMap>(this.configUrl).subscribe({
        next: (config) => {
          this.themes.set(config);
          this.applyTheme(this.activeThemeKey());
          resolve();
        },
        error: () => {
          // Fail gracefully — defaults from CSS custom properties will be used
          this.logger.warn('Could not load theme config; using CSS defaults.');
          resolve();
        },
      });
    });
  }

  /** Switch the active theme at runtime */
  setTheme(key: string): void {
    if (this.themes()[key]) {
      this.activeThemeKey.set(key);
      this.applyTheme(key);
    } else {
      this.logger.warn(`Theme "${key}" not found in config.`);
    }
  }

  /** Available theme keys */
  getThemeKeys(): string[] {
    return Object.keys(this.themes());
  }

  // ── private ─────────────────────────────────────────────
  private applyTheme(key: string): void {
    const theme = this.themes()[key];
    if (!theme) return;

    const root = document.documentElement;
    for (const [prop, value] of Object.entries(theme.colors)) {
      root.style.setProperty(prop, value);
    }
  }
}
